import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { sessionQuery } from "@/api/queries";
import { AppShell } from "@/components/AppShell";
import { AuditPage } from "@/pages/AuditPage";
import { ChatPage } from "@/pages/ChatPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { DashboardsPage } from "@/pages/DashboardsPage";
import { GuidePage } from "@/pages/GuidePage";
import { HomePage } from "@/pages/HomePage";
import { OperationsPage } from "@/pages/OperationsPage";
import {
  PrintConversationPage,
  PrintDashboardPage,
  PrintReportPage,
} from "@/pages/PrintPage";
import { PromptsPage } from "@/pages/PromptsPage";
import { ProvidersPage } from "@/pages/ProvidersPage";
import { ReportPage } from "@/pages/ReportPage";
import { ReportsPage } from "@/pages/ReportsPage";
import { SchedulesPage } from "@/pages/SchedulesPage";
import {
  SharedDashboardPage,
  SharedInactivePage,
  SharedReportPage,
} from "@/pages/SharedPage";
import { SourceDetailPage, type SourceTab } from "@/pages/SourceDetailPage";
import { SourcesPage } from "@/pages/SourcesPage";
import { ThemingPage } from "@/pages/ThemingPage";
import { UnlockPage } from "@/pages/UnlockPage";
import { UsagePage } from "@/pages/UsagePage";
import { UsersPage } from "@/pages/UsersPage";
import { EvalsPage } from "@/pages/EvalsPage";
import { WorkbenchPage } from "@/pages/WorkbenchPage";

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

/** Public: the unlock page, which bounces to the app once a session exists. */
const unlockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/unlock",
  component: UnlockPage,
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData({
      ...sessionQuery,
      revalidateIfStale: true,
    });
    if (session.authenticated) throw redirect({ to: "/" });
  },
});

/** Layout route: everything below it requires an unlocked session. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: AppShell,
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData({
      ...sessionQuery,
      revalidateIfStale: true,
    });
    if (!session.authenticated) {
      // A share session can read one thing; send it there rather than to a
      // form asking for a key it was never given (ACC-1).
      if (session.share) {
        throw redirect({
          to:
            session.share.subject_kind === "report"
              ? "/shared/report/$reportId"
              : "/shared/dashboard/$dashboardId",
          params: { reportId: session.share.subject_id, dashboardId: session.share.subject_id },
        });
      }
      throw redirect({ to: "/unlock" });
    }
  },
});

/**
 * The Admin tier's pages (ACC-2). An analyst who types the URL is sent home
 * rather than shown a page whose every request would be refused — but this
 * is a courtesy, not the control: the API refuses those requests itself.
 */
async function requireAdmin({ context }: { context: RouterContext }) {
  const session = await context.queryClient.ensureQueryData({
    ...sessionQuery,
    revalidateIfStale: true,
  });
  if (session.role !== "admin") throw redirect({ to: "/" });
}

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: HomePage,
});

/** The chat workspace, with and without a conversation selected. */
const chatIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chat",
  component: ChatPage,
});

const chatThreadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chat/$threadId",
  component: ChatPage,
});

/** Reports and dashboards (PRD FR-7). */
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  component: ReportsPage,
});

const reportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/$reportId",
  component: ReportPage,
  // ALERT-3: an alert email's "explain this" link. Opening it shows a confirm;
  // nothing runs, and nothing is spent, until somebody presses it.
  validateSearch: (search: Record<string, unknown>): { explain?: string } =>
    typeof search.explain === "string" && search.explain ? { explain: search.explain } : {},
});

const dashboardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboards",
  component: DashboardsPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboards/$dashboardId",
  component: DashboardPage,
});

const providersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/providers",
  beforeLoad: requireAdmin,
  component: ProvidersPage,
});

const schedulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/schedules",
  component: SchedulesPage,
});

const usageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/usage",
  beforeLoad: requireAdmin,
  component: UsagePage,
});

/** The M7 admin surfaces (PRD FR-11.1, FR-10.3, FR-10.4, FR-10.6, FR-4.10). */
const themingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/theming",
  beforeLoad: requireAdmin,
  component: ThemingPage,
});

/** Backlog item 10: the core prompt, versioned, with rollback (FR-2.2). */
const promptsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/prompts",
  beforeLoad: requireAdmin,
  component: PromptsPage,
});

/** EVAL-1: the eval harness. */
const evalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/evals",
  beforeLoad: requireAdmin,
  component: EvalsPage,
});

/** ACC-3: named accounts. */
const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/users",
  beforeLoad: requireAdmin,
  component: UsersPage,
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/audit",
  beforeLoad: requireAdmin,
  component: AuditPage,
});

const operationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/operations",
  beforeLoad: requireAdmin,
  component: OperationsPage,
});

/** The read-only SQL workbench (PRD FR-5.5) and the in-app guide (FR-12.2). */
const workbenchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/workbench",
  component: WorkbenchPage,
  // "The source you came from" (backlog item 7). Optional: the page falls back
  // to the source you last worked with, and only then to whatever exists.
  validateSearch: (search: Record<string, unknown>): { connection?: string } =>
    typeof search.connection === "string" && search.connection
      ? { connection: search.connection }
      : {},
});

const guideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/guide",
  component: GuidePage,
});

const sourcesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sources",
  component: SourcesPage,
});

const sourceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sources/$connectionId",
  component: SourceDetailPage,
  // Optional on purpose: a link to a source needs no search params, and the
  // page resolves the default itself (a CSV source has a tab a database has not).
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: SourceTab; relation?: string } => {
    const tab = search.tab as SourceTab | undefined;
    const known = (
      ["schema", "knowledge", "relationships", "health", "files", "settings"] as const
    ).includes(tab as SourceTab);
    // `relation` is how a SQL step in the trace opens the relation it read
    // (PRD FR-5.2).
    const relation = typeof search.relation === "string" ? search.relation : undefined;
    return { ...(known ? { tab } : {}), ...(relation ? { relation } : {}) };
  },
});

/**
 * The print routes (PRD FR-9.1, FR-9.2).
 *
 * Outside `appRoute` because a printed page has no sidebar, no header and
 * none of the viewer's controls — but **inside the same authentication**: the
 * renderer is a client like any other and presents a real session cookie, so
 * this checks exactly what every other route checks. A print route that
 * rendered without auth would be a public export of customer data.
 */
const printRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "print",
  component: () => (
    <div className="bg-bg min-h-screen">
      <Outlet />
    </div>
  ),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      ...sessionQuery,
      revalidateIfStale: true,
    });
    if (session.authenticated) return;
    // A shared PDF is printed by a renderer holding the share's own scope, so
    // it may print exactly the subject that share names — and nothing else.
    const share = session.share;
    if (share && location.pathname === `/print/${share.subject_kind}/${share.subject_id}`) return;
    throw redirect({ to: "/unlock" });
  },
});

/**
 * The shared view (ACC-1). Outside `appRoute` and outside the unlock guard:
 * a share session is not an unlocked deployment, and the inactive page has to
 * render for somebody holding nothing at all. Each subject route checks that
 * the session can read *this* subject; the API checks again regardless.
 */
const sharedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shared",
  component: () => <Outlet />,
});

async function guardShared(
  context: RouterContext,
  kind: "report" | "dashboard",
  subjectId: string,
) {
  const session = await context.queryClient.fetchQuery({ ...sessionQuery, staleTime: 0 });
  if (session.authenticated) return;
  if (session.share?.subject_kind === kind && session.share.subject_id === subjectId) return;
  throw redirect({ to: "/shared/inactive", search: {} });
}

const sharedReportRoute = createRoute({
  getParentRoute: () => sharedRoute,
  path: "/shared/report/$reportId",
  beforeLoad: ({ context, params }) => guardShared(context, "report", params.reportId),
  component: function SharedReportRoute() {
    const { reportId } = sharedReportRoute.useParams();
    return <SharedReportPage reportId={reportId} />;
  },
});

const sharedDashboardRoute = createRoute({
  getParentRoute: () => sharedRoute,
  path: "/shared/dashboard/$dashboardId",
  beforeLoad: ({ context, params }) => guardShared(context, "dashboard", params.dashboardId),
  component: function SharedDashboardRoute() {
    const { dashboardId } = sharedDashboardRoute.useParams();
    return <SharedDashboardPage dashboardId={dashboardId} />;
  },
});

const sharedInactiveRoute = createRoute({
  getParentRoute: () => sharedRoute,
  path: "/shared/inactive",
  validateSearch: (search: Record<string, unknown>): { reason?: string } =>
    typeof search.reason === "string" ? { reason: search.reason } : {},
  component: function SharedInactiveRoute() {
    const { reason } = sharedInactiveRoute.useSearch();
    return <SharedInactivePage reason={reason} />;
  },
});

/**
 * `?sql=1` appends the "Queries used" section (PRD FR-9.1).
 *
 * Compared against the **number** 1 as well as the string, because the router
 * JSON-parses search values: `?sql=1` arrives here as `1`, not `"1"`. A
 * string-only check silently produced a PDF with no appendix — byte-identical
 * to the one asked for without it — which is the M6 failure mode again: an
 * export that looks finished and quietly left something out. Found by
 * diffing the two PDFs the API returns, not by the type checker, because both
 * shapes are `unknown` here.
 */
const printSearch = (search: Record<string, unknown>): { sql?: boolean } => ({
  sql: search.sql === 1 || search.sql === true || search.sql === "1" || search.sql === "true",
});

const printReportRoute = createRoute({
  getParentRoute: () => printRoute,
  path: "/print/report/$reportId",
  validateSearch: printSearch,
  component: function PrintReportRoute() {
    const { reportId } = printReportRoute.useParams();
    const { sql } = printReportRoute.useSearch();
    return <PrintReportPage reportId={reportId} sql={Boolean(sql)} />;
  },
});

const printDashboardRoute = createRoute({
  getParentRoute: () => printRoute,
  path: "/print/dashboard/$dashboardId",
  validateSearch: printSearch,
  component: function PrintDashboardRoute() {
    const { dashboardId } = printDashboardRoute.useParams();
    const { sql } = printDashboardRoute.useSearch();
    return <PrintDashboardPage dashboardId={dashboardId} sql={Boolean(sql)} />;
  },
});

const printConversationRoute = createRoute({
  getParentRoute: () => printRoute,
  path: "/print/conversation/$threadId",
  validateSearch: printSearch,
  component: function PrintConversationRoute() {
    const { threadId } = printConversationRoute.useParams();
    const { sql } = printConversationRoute.useSearch();
    return <PrintConversationPage threadId={threadId} sql={Boolean(sql)} />;
  },
});

const routeTree = rootRoute.addChildren([
  unlockRoute,
  printRoute.addChildren([printReportRoute, printDashboardRoute, printConversationRoute]),
  sharedRoute.addChildren([sharedReportRoute, sharedDashboardRoute, sharedInactiveRoute]),
  appRoute.addChildren([
    homeRoute,
    chatIndexRoute,
    chatThreadRoute,
    sourcesRoute,
    sourceDetailRoute,
    reportsRoute,
    reportRoute,
    dashboardsRoute,
    dashboardRoute,
    providersRoute,
    schedulesRoute,
    usageRoute,
    themingRoute,
    promptsRoute,
    auditRoute,
    usersRoute,
    evalsRoute,
    operationsRoute,
    workbenchRoute,
    guideRoute,
  ]),
]);

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
